import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Column {
  key: string;
  label: string;
  render?: (value: any, row: any) => React.ReactNode;
  className?: string; // extra classes for <th>/<td>
}

interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface DataTableProps {
  canEdit?: boolean;
  data: any[];
  columns: Column[];
  height?: number; // px max-height for the scroll container
  loading?: boolean;
  emptyMessage?: string;
  minLoadingTime?: number; // ms
  onRowClick?: (row: any) => void;
  pagination?: PaginationInfo | null;
  onPageChange?: (page: number) => void;
  compact?: boolean; // smaller padding and font for dense tables
}

export const DataTable: React.FC<DataTableProps> = ({
  canEdit,
  data,
  columns,
  height,
  loading = false,
  emptyMessage = 'No data found',
  minLoadingTime = 500,
  onRowClick,
  pagination,
  onPageChange,
  compact = false,
}) => {
  const thPadding = compact ? 'px-3 py-1.5' : 'px-6 py-3';
  const tdPadding = compact ? 'px-3 py-1.5' : 'px-6 py-4';
  const fontSize = compact ? 'text-xs' : 'text-sm';
  const [showLoading, setShowLoading] = useState(loading);
  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null);

  useEffect(() => {
    if (loading) {
      setShowLoading(true);
      setLoadingStartTime(Date.now());
      return;
    }

    if (loadingStartTime) {
      const elapsed = Date.now() - loadingStartTime;
      const remaining = Math.max(0, minLoadingTime - elapsed);
      if (remaining > 0) {
        const t = setTimeout(() => {
          setShowLoading(false);
          setLoadingStartTime(null);
        }, remaining);
        return () => clearTimeout(t);
      }
    }
    setShowLoading(false);
    setLoadingStartTime(null);
  }, [loading, minLoadingTime, loadingStartTime]);

  const handlePageChange = (newPage: number) => {
    if (!onPageChange || !pagination) return;
    if (newPage >= 1 && newPage <= (pagination.totalPages || 1)) {
      onPageChange(newPage);
    }
  };

  const getFileType = (filename: string): string => {
    if (!filename) return 'Unknown';
    const extension = filename.toLowerCase().split('.').pop();
    switch (extension) {
      case 'pdf':
        return 'PDF';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'svg':
        return 'Image';
      case 'doc':
      case 'docx':
        return 'Document';
      case 'xls':
      case 'xlsx':
        return 'Spreadsheet';
      default:
        return 'File';
    }
  };

  return (
    <div className="w-full">
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700">
        {/* Scroll container (keeps header sticky) */}
        <div
          className={`w-full overflow-x-auto ${height ? 'overflow-y-auto' : ''}`}
          style={height ? { maxHeight: height } : undefined}>
          <table className="w-full border-collapse">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 sticky top-0 z-10">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={`${thPadding} text-left ${fontSize} font-semibold text-gray-700 dark:text-gray-300 ${column.className || ''}`}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100 dark:divide-gray-600">
              {showLoading ? (
                // Loading skeleton rows
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`skeleton-${i}`} className="animate-pulse">
                    {columns.map((col, j) => (
                      <td
                        key={`skeleton-td-${i}-${j}`}
                        className={`${tdPadding} ${col.className || ''}`}>
                        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className={`${tdPadding} py-12 text-center text-gray-500 dark:text-gray-400 ${fontSize}`}>
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                data.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 ${onRowClick ? 'cursor-pointer' : ''}`}
                    onClick={() => onRowClick?.(row)}>
                    {columns.map((column) => {
                      const value =
                        column.key === 'name' && row?.category === 'type'
                          ? getFileType(row[column.key])
                          : row[column.key];

                      return (
                        <td
                          key={column.key}
                          className={`${tdPadding} align-middle ${fontSize} text-gray-900 dark:text-gray-100 ${column.className || ''}`}>
                          {column.render
                            ? column.render(value, row)
                            : (value ?? 'N/A')}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6">
            {/* Mobile */}
            <div className="flex flex-1 justify-between sm:hidden">
              <button type="button"
                onClick={() => handlePageChange((pagination.page || 1) - 1)}
                disabled={!pagination.hasPrev}
                className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed">
                Previous
              </button>
              <button type="button"
                onClick={() => handlePageChange((pagination.page || 1) + 1)}
                disabled={!pagination.hasNext}
                className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed">
                Next
              </button>
            </div>

            {/* Desktop */}
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing{' '}
                  <span className="font-medium">
                    {(pagination.page - 1) * pagination.limit + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total,
                    )}
                  </span>{' '}
                  of <span className="font-medium">{pagination.total}</span>{' '}
                  results
                </p>
              </div>
              <div>
                <nav
                  className="isolate inline-flex -space-x-px rounded-md shadow-xs"
                  aria-label="Pagination">
                  <button type="button"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrev}
                    className="relative inline-flex items-center bg-white dark:bg-gray-700 rounded-l-md px-2 py-2 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed">
                    <span className="sr-only">Previous</span>
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {Array.from(
                    { length: Math.min(5, pagination.totalPages) },
                    (_, i) => {
                      const pageNum = i + 1;
                      const isCurrent = pageNum === pagination.page;
                      return (
                        <button type="button"
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          aria-current={isCurrent ? 'page' : undefined}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-medium -ml-px focus:z-20 focus:outline-offset-0 ${
                            isCurrent
                              ? 'z-10 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700'
                              : 'text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}>
                          {pageNum}
                        </button>
                      );
                    },
                  )}

                  <button type="button"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNext}
                    className="relative inline-flex items-center rounded-r-md px-2 py-2 bg-white dark:bg-gray-700 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-600 -ml-px hover:bg-gray-50 dark:hover:bg-gray-600 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed">
                    <span className="sr-only">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
